import { useMemo } from 'react'
import { useTranslate } from '@ur-solutions/react-hooks'

import { cn } from '@/lib/utils'
import { format } from '@/utils/date-fns'
import { FAIcon } from '@/components/fa-icon/fa-icon'

import { useIssueActionString } from '../../hooks'
import type { IssueActionLog } from '../../types.graphl'
import { getActionVisual } from './action-visuals'
import { ActionLogDiff } from './action-log-diff'
import { ExportChip } from './export-chip'
import { TimelineRail } from './timeline-rail'
import { parseChangedFields } from './util'

/**
 * One action-log entry. Renders the rail dot + header, then degrades by what
 * the row carries: a field diff, an export chip, or a plain "no details" note.
 */
export const ActionLogEntry = ({
  node,
  isLast,
}: {
  node: IssueActionLog
  isLast: boolean
}) => {
  const actionString = useIssueActionString(node)
  const t = useTranslate({ noDetails: 'issues.action-log.no-field-details' })

  const fields = useMemo(() => {
    const changed = parseChangedFields(node.metadata)
    return changed ? Object.entries(changed) : []
  }, [node.metadata])

  const visual = getActionVisual(node.action)
  const timestamp = format(new Date(node.createdAt), 'd MMM yyyy, HH:mm')

  return (
    <div className="flex gap-3.5">
      <TimelineRail isLast={isLast}>
        <div
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-full',
            visual.bg
          )}
        >
          <FAIcon icon={visual.icon} className={cn('text-xs', visual.color)} />
        </div>
      </TimelineRail>

      <div className={cn('flex flex-1 flex-col gap-2', !isLast && 'pb-6')}>
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm text-muted-foreground">{actionString}</span>
          <span className="shrink-0 text-xs text-tertiary">{timestamp}</span>
        </div>

        {fields.length > 0 ? (
          <ActionLogDiff fields={fields} />
        ) : node.action === 'EXPORTED' ? (
          <ExportChip metadata={node.metadata} />
        ) : (
          <span className="text-xs italic text-tertiary">{t.noDetails}</span>
        )}
      </div>
    </div>
  )
}
